import { NgClass } from '@angular/common';
import { Component, effect, inject, input, output, signal, untracked } from '@angular/core';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRange,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { BASE_CONFIG } from '@libs/core/base-config';
import { CategoryStore } from '@libs/mtjr/shared/categories/cateogy.store';
import { Dtos, PublishStatusList } from '@libs/mtjr/shared/generated/types';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'mtjr-vendor-filter-sheet',
  providers: [CategoryStore],
  imports: [
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonLabel,
    IonSelect,
    IonSelectOption,
    IonItem,
    NgClass,
    IonIcon,
    IonList,
    IonCard,
    IonHeader,
    IonRange,
    TranslatePipe,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar class="px-4">
        <ion-title mode="ios">{{ 'VENDORS.FILTER' | translate }}</ion-title>
        <ion-buttons slot="start">
          <ion-button color="primary" (click)="modalCtrl.dismiss()">{{ 'COMMON.CLOSE' | translate }}</ion-button>
        </ion-buttons>

        <ion-buttons slot="end">
          <ion-button (click)="clearFilters()">{{ 'COMMON.CLEAR_FILTERS' | translate }}</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="h-screen">
      <ion-list class="bg-transparent *:mx-2">
        @if (isVendorOwner) {
          <ion-card class="!mb-0 !mt-2">
            <ion-item lines="none">
              <ion-select
                interface="popover"
                [label]="'COMMON.STATUS' | translate"
                [placeholder]="'COMMON.SELECT_STATUS' | translate"
                class="capitalize"
                mode="ios"
                [value]="queryInput().publishStatus"
                (ionChange)="onStatusChanged($event)"
                [interfaceOptions]="{ arrow: false }"
              >
                <ion-select-option value="-1" mode="ios">
                  {{ 'COMMON.ALL' | translate }}
                </ion-select-option>
                @for (publishStatus of publishStatusList; track $index) {
                  <ion-select-option
                    [ngClass]="{
                      'text-success': publishStatus === 'published',
                      'text-warning': publishStatus === 'waitingForReview' || publishStatus === 'inReview',
                      'text-danger': publishStatus === 'rejected' || publishStatus === 'unPublished',
                      'text-medium': publishStatus === 'draft',
                    }"
                    [value]="publishStatus"
                    class="capitalize"
                  >
                    <div class="flex bg-red-400">
                      <span>{{ 'ENUMS.PublishStatus.' + publishStatus | translate }}</span>
                    </div>
                  </ion-select-option>
                }
              </ion-select>
            </ion-item>
          </ion-card>
        }
        <ion-card class="!mb-0 !mt-2">
          <ion-item lines="none">
            <ion-select
              mode="ios"
              [interfaceOptions]="{ arrow: false }"
              interface="popover"
              label="{{ 'PRODUCTS.PRODUCT_CATEGORY' | translate }}"
              placeholder="{{ 'COMMON.SELECT_CATEGORY' | translate }}"
              class="capitalize"
              [value]="queryInput().category"
              (ionChange)="onCategoryChanged($event)"
            >
              <ion-select-option value="-1">{{ 'COMMON.ALL' | translate }}</ion-select-option>
              @for (category of categoryStore.categories(); track $index) {
                <ion-select-option [value]="category.id">
                  {{ category.name }}
                </ion-select-option>
              }
            </ion-select>
          </ion-item>
        </ion-card>

        <ion-card class="!mb-0 !mt-2">
          <ion-item lines="none">
            <ion-label>{{ 'PRODUCTS.PRICE_RANGE' | translate }}</ion-label>
            <span slot="end">{{ queryInput().priceFrom }} - {{ queryInput().priceTo }}</span>
          </ion-item>

          <div class="!px-8">
            <ion-range
              [dualKnobs]="true"
              [max]="100000"
              [min]="10"
              [value]="{ lower: queryInput().priceFrom, upper: queryInput().priceTo }"
              (ionInput)="onRangeChanged($event)"
            />
          </div>
        </ion-card>

        <ion-card class="!mb-0 !mt-2">
          <ion-item lines="none">
            <ion-label>{{ 'PRODUCTS.RATING' | translate }}</ion-label>
            <div class="flex" slot="end">
              @for (i of [1, 2, 3, 4, 5]; track $index) {
                <ion-button fill="clear" class="me-1" (click)="onStarsChanged(i)" shape="round" color="warning">
                  @if ((queryInput().stars ?? 0) >= i) {
                    <ion-icon slot="icon-only" class="text-2xl" color="warning" name="star-filled" />
                  } @else {
                    <ion-icon slot="icon-only" class="text-2xl" color="warning" name="star" />
                  }
                </ion-button>
              }
            </div>
          </ion-item>
        </ion-card>
      </ion-list>
    </ion-content>
  `,
})
export class VendorFilterSheetComponent {
  modalCtrl = inject(ModalController);
  publishStatusList = PublishStatusList;
  isVendorOwner = inject(BASE_CONFIG).flavor === 'vendors';
  categoryStore = inject(CategoryStore);
  initialQuery = input<Dtos.ProductsListQueryParams>();
  queryChanged = output<Dtos.ProductsListQueryParams>();
  modal = input<IonModal>();

  queryInput = signal<Dtos.ProductsListQueryParams>({
    page: 1,
    totalPerPage: 10,
  });

  onStatusChanged(event: CustomEvent) {
    const value = event.detail.value;
    const publishStatus = value === '-1' ? undefined : value;
    this.queryInput.set({ ...this.queryInput(), publishStatus });
    this.queryChanged.emit(this.queryInput());
  }

  onCategoryChanged(event: CustomEvent) {
    const value = event.detail.value;
    const category = value === '-1' ? undefined : value;
    this.queryInput.set({ ...this.queryInput(), category });
    this.queryChanged.emit(this.queryInput());
  }

  onRangeChanged(event: CustomEvent) {
    const { lower, upper } = event.detail.value;
    this.queryInput.set({ ...this.queryInput(), priceFrom: lower, priceTo: upper });
    this.queryChanged.emit(this.queryInput());
  }

  onStarsChanged(stars: number) {
    this.queryInput.set({ ...this.queryInput(), stars });
    this.queryChanged.emit(this.queryInput());
  }

  clearFilters() {
    this.queryInput.set({
      ...this.queryInput(),
      publishStatus: undefined,
      category: undefined,
      priceFrom: 10,
      priceTo: 100000,
      stars: undefined,
    });
    this.queryChanged.emit(this.queryInput());
  }

  constructor() {
    this.categoryStore.categoriesListPage();

    effect(() => {
      const initial = this.initialQuery();
      untracked(() => {
        this.queryInput.set({
          ...this.queryInput(),
          ...initial,
          priceFrom: initial?.priceFrom ?? 10,
          priceTo: initial?.priceTo ?? 100000,
        });
      });
    });

    effect(() => {
      const modal = this.modal();
      if (modal) {
        modal.onWillDismiss().then(() => {
          // ugly but needed to ensure the popover is dismissed
          // find element by tag ion-popover and dismiss it
          document.querySelectorAll('ion-popover').forEach((popover) => {
            (popover as HTMLIonPopoverElement).dismiss();
          });
        });
      }
    });
  }
}
